import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Home,
  Wallet,
  Sun,
  ShieldCheck,
  Snowflake,
  Refrigerator,
  Tv,
  Lightbulb,
  WashingMachine,
  Droplet,
  Wind,
  CircleCheck,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=2000&q=85&auto=format&fit=crop";

export default function Residential() {
  const { t } = useTranslation();
  const [leadOpen, setLeadOpen] = useState(false);
  const openQuote = () => setLeadOpen(true);

  const APPLIANCES = [
    { icon: Snowflake, name: t("res.app_ac"), units: t("res.app_ac_units"), cost: "₹2,400/mo" },
    { icon: Refrigerator, name: t("res.app_fridge"), units: t("res.app_fridge_units"), cost: "₹700/mo" },
    { icon: Droplet, name: t("res.app_geyser"), units: t("res.app_geyser_units"), cost: "₹800/mo" },
    { icon: WashingMachine, name: t("res.app_washer"), units: t("res.app_washer_units"), cost: "₹400/mo" },
    { icon: Tv, name: t("res.app_tv"), units: t("res.app_tv_units"), cost: "₹300/mo" },
    { icon: Wind, name: t("res.app_fans"), units: t("res.app_fans_units"), cost: "₹600/mo" },
    { icon: Lightbulb, name: t("res.app_lights"), units: t("res.app_lights_units"), cost: "₹200/mo" },
  ];

  const BENEFITS = [
    { icon: Wallet, slug: "b1", title: t("res.b1_t"), desc: t("res.b1_d") },
    { icon: ShieldCheck, slug: "b2", title: t("res.b2_t"), desc: t("res.b2_d") },
    { icon: Sun, slug: "b3", title: t("res.b3_t"), desc: t("res.b3_d") },
    { icon: Sparkles, slug: "b4", title: t("res.b4_t"), desc: t("res.b4_d") },
    { icon: Home, slug: "b5", title: t("res.b5_t"), desc: t("res.b5_d") },
    { icon: Lightbulb, slug: "b6", title: t("res.b6_t"), desc: t("res.b6_d") },
  ];

  const STATS = [
    { k: "₹78,000", v: t("res.stat_subsidy") },
    { k: "70–95%", v: t("res.stat_reduction") },
    { k: "4–5 yrs", v: t("res.stat_payback") },
    { k: "25 yrs", v: t("res.stat_warranty") },
  ];

  const STEPS = [
    { n: "01", t: t("res.s1_t"), d: t("res.s1_d") },
    { n: "02", t: t("res.s2_t"), d: t("res.s2_d") },
    { n: "03", t: t("res.s3_t"), d: t("res.s3_d") },
    { n: "04", t: t("res.s4_t"), d: t("res.s4_d") },
  ];

  const FAQS = [
    { q: t("res.f1_q"), a: t("res.f1_a") },
    { q: t("res.f2_q"), a: t("res.f2_a") },
    { q: t("res.f3_q"), a: t("res.f3_a") },
  ];

  return (
    <div className="relative bg-white">
      <Navbar onCtaClick={openQuote} />

      <section
        data-testid="residential-hero"
        className="relative pt-[88px] min-h-[80vh] flex items-end overflow-hidden bg-stone-950"
      >
        <img src={HERO_IMG} alt="Residential rooftop solar" className="absolute inset-0 w-full h-full object-cover opacity-65" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase">
            <Home className="w-3.5 h-3.5" /> {t("res.hero_badge")}
          </div>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
            {t("res.hero_title_a")}<br />
            <span className="text-orange-400">{t("res.hero_title_b")}</span>
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg leading-relaxed">{t("res.hero_subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={openQuote} data-testid="residential-cta-quote" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("res.cta_quote")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/#calculator" data-testid="residential-cta-calc" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-white/30 bg-white/5 text-white font-semibold hover:bg-white/10 transition" reloadDocument>
              {t("res.cta_calc")}
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`residential-stat-${i}`}>
              <div className="font-display font-extrabold text-3xl md:text-4xl text-green-700 leading-none">{s.k}</div>
              <div className="mt-2 text-stone-600 text-sm leading-snug">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("res.app_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("res.app_title_a")} <span className="text-green-700">{t("res.app_title_b")}</span>
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed text-[15px]">{t("res.app_subtitle")}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {APPLIANCES.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} data-testid={`residential-appliance-${i}`} className="p-5 rounded-2xl border border-stone-200 bg-white hover:border-green-300 transition group">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-green-100 grid place-items-center group-hover:bg-green-600 transition">
                      <Icon className="w-5 h-5 text-green-700 group-hover:text-white transition" strokeWidth={2.2} />
                    </div>
                    <div className="font-display font-bold text-stone-900 leading-tight">{a.name}</div>
                  </div>
                  <div className="mt-3 text-xs text-stone-500 uppercase tracking-wider">{t("res.app_consumption")}</div>
                  <div className="text-sm font-semibold text-stone-800">{a.units}</div>
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 rounded-full px-2.5 py-1 text-xs font-bold">
                    {t("res.app_current_bill")}: {a.cost}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-stone-500 max-w-2xl">{t("res.app_note")}</p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("res.b_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("res.b_title_a")} <span className="text-green-700">{t("res.b_title_b")}</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.slug} data-testid={`residential-benefit-${b.slug}`} className="premium-card group p-7 rounded-3xl border border-stone-200 bg-white hover:border-green-300 hover:premium-shadow transition">
                  <div className="w-12 h-12 rounded-2xl bg-green-100 grid place-items-center group-hover:bg-green-600 transition">
                    <Icon className="w-6 h-6 text-green-700 group-hover:text-white transition" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl text-stone-900">{b.title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-[15px]">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("res.s_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("res.s_title_a")} <span className="text-green-700">{t("res.s_title_hl")}</span> {t("res.s_title_b")}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((p) => (
              <div key={p.n} data-testid={`residential-step-${p.n}`} className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
                <div className="font-handwritten text-5xl text-green-700 leading-none">{p.n}</div>
                <h3 className="mt-3 font-display font-bold text-lg text-stone-900">{p.t}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("res.f_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("res.f_title_a")} <span className="text-green-700">{t("res.f_title_hl")}</span>
            </h2>
          </div>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`residential-faq-${i}`} className="group p-6 rounded-2xl bg-white border border-stone-200 open:border-green-300 open:shadow-md transition">
                <summary className="cursor-pointer list-none font-display font-bold text-lg text-stone-900 flex items-start justify-between gap-4">
                  {f.q}
                  <span className="text-green-600 text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-stone-600 leading-relaxed text-[15px]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl text-stone-900 leading-tight">
            {t("res.final_a")} <span className="text-green-700">{t("res.final_hl")}</span>
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl mx-auto">{t("res.final_sub")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={openQuote} data-testid="residential-cta-final" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("res.final_cta")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-stone-300 bg-white text-stone-800 font-semibold hover:bg-stone-100 transition">
              {t("res.back_home")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        context={{ segment: "residential" }}
        title={t("res.lead_title")}
        subtitle={t("res.lead_subtitle")}
        source="residential_page"
      />
    </div>
  );
}
